import React from "react";
import logoStacked from "@/assets/logo-stacked.png";
import logoStackedLight from "@/assets/logo-stacked-light.png";
import logoHorizontal from "@/assets/logo-horizontal.png";
import logoEmblem from "@/assets/logo-emblem.png";

const cx = (...c) => c.filter(Boolean).join(" ");

export const LOGO_SRC = { stacked: logoStacked, "stacked-light": logoStackedLight, horizontal: logoHorizontal, emblem: logoEmblem };

export const Logo = ({ variant = "stacked", light = false, className = "", ...props }) => {
  const key = variant === "stacked" && light ? "stacked-light" : variant;
  return (
    <img
      src={LOGO_SRC[key]}
      alt="Sincerr — Be Sincere."
      className={cx("select-none object-contain", className)}
      draggable={false}
      data-testid="sincerr-logo"
      {...props}
    />
  );
};

/* Backwards-compatible text-free wordmark: renders the official stacked logo. */
export const Wordmark = ({ size = "md", light = false, className = "" }) => {
  const sizes = { sm: "h-9", md: "h-14", lg: "h-20" };
  return (
    <span data-testid="sincerr-wordmark" className={cx("inline-block", className)}>
      <Logo variant="stacked" light={light} className={sizes[size]} />
    </span>
  );
};

export const Button = React.forwardRef(
  ({ variant = "primary", size = "md", className = "", children, ...props }, ref) => {
    const base =
      "inline-flex items-center justify-center gap-2 font-sans font-medium rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#253B35]/40 disabled:opacity-50 disabled:pointer-events-none";
    const variants = {
      primary: "bg-[#253B35] text-[#F8F6F1] hover:bg-[#1D302A]",
      outline: "border border-[#253B35]/30 text-[#253B35] hover:bg-[#253B35]/5",
      ghost: "text-[#253B35] hover:bg-[#253B35]/5",
      soft: "bg-[#F1EEE7] text-[#253B35] hover:bg-[#E3DCD0]",
      danger: "border border-[#9B4B45]/40 text-[#9B4B45] hover:bg-[#9B4B45]/5",
    };
    const sizes = { sm: "text-sm px-4 py-2", md: "text-sm px-6 py-3", lg: "text-base px-8 py-3.5", icon: "p-2.5" };
    return (
      <button ref={ref} className={cx(base, variants[variant], sizes[size], className)} {...props}>
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

export const Field = ({ label, hint, error, children, className = "" }) => (
  <div className={cx("space-y-1.5", className)}>
    {label && <label className="block font-sans text-sm font-medium text-[#1F2B26]">{label}</label>}
    {children}
    {hint && !error && <p className="font-sans text-xs text-[#8A9790]">{hint}</p>}
    {error && <p className="font-sans text-xs text-[#9B4B45]" data-testid="field-error">{error}</p>}
  </div>
);

export const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={cx(
      "w-full rounded-xl border border-[#E3DCD0] bg-white px-4 py-3 text-[15px] text-[#1F2B26] placeholder:text-[#B2BAB4]",
      "focus:outline-none focus:border-[#253B35] focus:ring-2 focus:ring-[#253B35]/10 transition-colors",
      className
    )}
    {...props}
  />
));
Input.displayName = "Input";

export const Textarea = React.forwardRef(({ className = "", ...props }, ref) => (
  <textarea
    ref={ref}
    className={cx(
      "w-full rounded-xl border border-[#E3DCD0] bg-white px-4 py-3 text-[15px] text-[#1F2B26] placeholder:text-[#B2BAB4] resize-none",
      "focus:outline-none focus:border-[#253B35] focus:ring-2 focus:ring-[#253B35]/10 transition-colors",
      className
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

export const Chip = ({ active, children, className = "", ...props }) => (
  <button
    type="button"
    className={cx(
      "rounded-full border px-4 py-2 text-sm font-sans transition-colors duration-150",
      active ? "bg-[#253B35] text-[#F8F6F1] border-[#253B35]" : "bg-white text-[#5F6D66] border-[#E3DCD0] hover:border-[#8B9D90]",
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export const Card = ({ className = "", children, ...props }) => (
  <div className={cx("rounded-2xl bg-white border border-[#E3DCD0]", className)} {...props}>
    {children}
  </div>
);

export const Spinner = ({ className = "" }) => (
  <div className={cx("h-6 w-6 rounded-full border-2 border-[#8B9D90]/30 border-t-[#253B35] animate-spin", className)} />
);
